import { UnsubscribeFunctionsManagerDelegate } from "@lib/utils/UnsubscribeFunctionsManagerDelegate";

import { GroupDelegate } from "../../delegates/GroupDelegate";
import { ItemDelegate } from "../../delegates/ItemDelegate";
import { SharedSettingsDelegate, SharedSettingsDelegateTopic } from "../../delegates/SharedSettingsDelegate";
import type { GroupType } from "../../groups/groupTypes";
import type {
    CustomGroupImplementation,
    CustomGroupImplementationWithSettings,
} from "../../interfacesAndTypes/customGroupImplementation";
import { includesSettings } from "../../interfacesAndTypes/customGroupImplementation";
import type { SetupBasicBindingsContext } from "../../interfacesAndTypes/customSettingsHandler";
import type { ItemGroup } from "../../interfacesAndTypes/entities";
import type { SerializedGroup, SerializedSettingsState } from "../../interfacesAndTypes/serialization";
import { SerializedType } from "../../interfacesAndTypes/serialization";
import type { MakeSettingTypesMap, SettingsKeysFromTuple } from "../../interfacesAndTypes/utils";
import type { Settings } from "../../settings/settingsDefinitions";
import { DataProviderManagerTopic, type DataProviderManager } from "../DataProviderManager/DataProviderManager";
import type { SettingManager } from "../SettingManager/SettingManager";
import { makeSettings } from "../utils/makeSettings";

// Using a unique brand to identify Group objects, since instanceof checks won't work due to potential multiple versions of the module.
// Using Symbol.for to ensure that even if there are multiple versions of the module, they will all reference the same symbol for the brand.
const GROUP_BRAND = Symbol.for("dpf/group");

export function isGroup(obj: any): obj is Group {
    return typeof obj === "object" && obj !== null && GROUP_BRAND in obj;
}

export type GroupParams<
    TSettingTypes extends Settings,
    TSettings extends MakeSettingTypesMap<TSettingTypes> = MakeSettingTypesMap<TSettingTypes>,
> = {
    dataProviderManager: DataProviderManager;
    color?: string;
    type: GroupType;
    customGroupImplementation:
        | CustomGroupImplementation
        | CustomGroupImplementationWithSettings<TSettingTypes, TSettings>;
};

export class Group<
    TSettings extends Settings = [],
    TSettingTypes extends MakeSettingTypesMap<TSettings> = MakeSettingTypesMap<TSettings>,
    TSettingKey extends SettingsKeysFromTuple<TSettings> = SettingsKeysFromTuple<TSettings>,
> implements ItemGroup {
    private readonly [GROUP_BRAND] = true;

    private _itemDelegate: ItemDelegate;
    private _groupDelegate: GroupDelegate;
    private _type: GroupType;
    private _icon: React.ReactNode | null = null;
    private _emptyContentMessage: string | null = null;
    private _sharedSettingsDelegate: SharedSettingsDelegate<TSettings, TSettingTypes, TSettingKey> | null = null;
    private _unsubscribeFunctionsManagerDelegate: UnsubscribeFunctionsManagerDelegate =
        new UnsubscribeFunctionsManagerDelegate();

    constructor(params: GroupParams<TSettings, TSettingTypes>) {
        const { dataProviderManager, customGroupImplementation, type } = params;
        this._groupDelegate = new GroupDelegate(this);
        this._groupDelegate.setColor(dataProviderManager.makeGroupColor());
        this._itemDelegate = new ItemDelegate(customGroupImplementation.getDefaultName(), 1, dataProviderManager);
        if (includesSettings(customGroupImplementation)) {
            this._sharedSettingsDelegate = new SharedSettingsDelegate<TSettings, TSettingTypes, TSettingKey>(
                this,
                makeSettings<TSettings, TSettingTypes, TSettingKey>(
                    customGroupImplementation.settings as unknown as TSettings,
                    customGroupImplementation.getDefaultSettingsValues?.() ?? {},
                ),
                customGroupImplementation.setupBindings as unknown as
                    | ((args: SetupBasicBindingsContext<TSettings, TSettingTypes>) => void)
                    | undefined,
            );
            this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
                "shared-settings",
                this._sharedSettingsDelegate
                    .getPublishSubscribeDelegate()
                    .makeSubscriberFunction(SharedSettingsDelegateTopic.SETTINGS_CHANGED)(() =>
                    this.handleSettingsChange(),
                ),
            );
        }
        this._type = type;
        this._emptyContentMessage = customGroupImplementation.getEmptyContentMessage
            ? customGroupImplementation.getEmptyContentMessage()
            : null;
    }

    handleSettingsChange() {
        this._itemDelegate.getDataProviderManager().publishTopic(DataProviderManagerTopic.DATA_REVISION);
    }

    getItemDelegate(): ItemDelegate {
        return this._itemDelegate;
    }

    getGroupDelegate(): GroupDelegate {
        return this._groupDelegate;
    }

    getIcon(): React.ReactNode {
        return this._icon;
    }

    getEmptyContentMessage(): string | null {
        return this._emptyContentMessage;
    }

    getSharedSettingsDelegate(): SharedSettingsDelegate<TSettings, TSettingTypes, TSettingKey> | null {
        return this._sharedSettingsDelegate;
    }

    getWrappedSettings(): { [K in TSettingKey]: SettingManager<K> } {
        if (!this._sharedSettingsDelegate) {
            throw new Error("Group does not have shared settings.");
        }
        return this._sharedSettingsDelegate.getWrappedSettings();
    }

    getGroupType(): GroupType {
        return this._type;
    }

    serializeState(): SerializedGroup<TSettings, TSettingKey> {
        return {
            ...this._itemDelegate.serializeState(),
            type: SerializedType.GROUP,
            groupType: this._type,
            color: this._groupDelegate.getColor() ?? "",
            settings:
                this._sharedSettingsDelegate?.serializeSettings() ??
                ({} as SerializedSettingsState<TSettings, TSettingKey>),
            children: this._groupDelegate.serializeChildren(),
        };
    }

    deserializeState(serialized: SerializedGroup<TSettings, TSettingKey>) {
        this._itemDelegate.deserializeState(serialized);
        this._groupDelegate.setColor(serialized.color);
        this._groupDelegate.deserializeChildren(serialized.children);
        const reportError = (errorMsg: string) => {
            this.getItemDelegate().reportDeserializationError(errorMsg);
        };
        this._sharedSettingsDelegate?.deserializeSettings(serialized.settings, reportError);
    }

    beforeDestroy(): void {
        this._groupDelegate.beforeDestroy();
        this._sharedSettingsDelegate?.beforeDestroy();
        this._unsubscribeFunctionsManagerDelegate.unsubscribeAll();
    }
}
